import { Button, Flex, Heading, Skeleton, Spacer } from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { useMemo } from "react";
import { GenericError } from "src/components/Feedback/GenericError";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import {
  NavigationBlocker,
  NavigationBlockerProvider,
} from "src/components/NavigationBlocker";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { validateWithZod } from "src/services/formValidations";
import * as RD from "src/types/remoteData";
import { z } from "zod";
import * as Alerts from "./Alerts";
import * as ExternalCapacity from "./ExternalCapacity";
import * as LandingPage from "./LandingPage";
import * as Languages from "./Languages";
import * as Payments from "./Payments";

const FormikSchema = Languages.FormikSchema.merge(Alerts.FormikSchema)
  .merge(Payments.FormikSchema)
  .merge(ExternalCapacity.FormikSchema)
  .merge(LandingPage.FormikSchema);

type FormikType = z.infer<typeof FormikSchema>;

export const ToolsTab = () => {
  const languages = Languages.useService();
  const alerts = Alerts.useService();
  const payments = Payments.useService();
  const externalCapacity = ExternalCapacity.useService();
  const landingPage = LandingPage.useService();

  const remoteData = useMemo(() => {
    return RD.toTuple5(
      languages.initialValues,
      alerts.initialValues,
      payments.initialValues,
      externalCapacity.initialValues,
      landingPage.initialValues
    ).map(
      ([
        translationInitialValues,
        alertsInitialValues,
        paymentsInitialValues,
        externalCapacityInitialValues,
        landingPageInitialValues,
      ]) => {
        return {
          ...translationInitialValues,
          ...alertsInitialValues,
          ...paymentsInitialValues,
          ...externalCapacityInitialValues,
          ...landingPageInitialValues,
        };
      }
    );
  }, [
    languages.initialValues,
    alerts.initialValues,
    payments.initialValues,
    externalCapacity.initialValues,
    landingPage.initialValues,
  ]);

  const toast = useAvelaToast();

  return (
    <RemoteDataView
      remoteData={remoteData}
      error={() => <GenericError />}
      loading={<Loading />}
      reloading={null}
    >
      {(initialValues) => {
        return (
          <NavigationBlockerProvider>
            <Formik<FormikType>
              initialValues={initialValues}
              validate={validateWithZod(FormikSchema)}
              onSubmit={async (values, formik) => {
                try {
                  await Promise.all([
                    languages.submit(values),
                    alerts.submit(values),
                    payments.submit(values),
                    externalCapacity.submit(values),
                    landingPage.submit(values),
                  ]);

                  toast({
                    title: "Organization updated",
                    isClosable: true,
                    status: "success",
                  });
                  formik.resetForm({ values });
                } catch (error) {
                  console.error(error);
                  toast.error({
                    title: "Error editing organization",
                    description:
                      "Please try again later or report the problem to our support team",
                  });
                }
              }}
            >
              {(formik) => {
                return (
                  <Flex
                    as={Form}
                    direction="column"
                    height="100%"
                    grow="1"
                    gap={10}
                  >
                    <Heading as="h2" variant="admin">
                      General settings
                    </Heading>
                    <Languages.Form />
                    <Alerts.Form />
                    <Payments.Form />
                    <LandingPage.Form />
                    <ExternalCapacity.Form />
                    <Spacer />
                    <AdminFormButtons insideTabPanel>
                      <Spacer />
                      <Button
                        type="submit"
                        isLoading={formik.isSubmitting}
                        isDisabled={!formik.isValid || !formik.dirty}
                      >
                        Update
                      </Button>
                      <NavigationBlocker hasChanges={formik.dirty} />
                    </AdminFormButtons>
                  </Flex>
                );
              }}
            </Formik>
          </NavigationBlockerProvider>
        );
      }}
    </RemoteDataView>
  );
};

const Loading = () => {
  return (
    <Flex direction="column" gap={6}>
      <Skeleton height="2rem" width="15rem" />
      <Skeleton height="4rem" width="100%" />
      <Skeleton height="4rem" width="100%" />
      <Skeleton height="4rem" width="100%" />
      <Spacer height="1rem" />
      <Skeleton height="2rem" width="15rem" />
      <Skeleton height="4rem" width="100%" />
      <Skeleton height="4rem" width="100%" />
      <Skeleton height="4rem" width="100%" />
    </Flex>
  );
};
